import { DiscountCode, TicketType } from "@prisma/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { CreateTicketTypeForm } from "../schemas/tickets/create-ticket-type.schema";
import { privateAxios } from "../axiosInstances";
import { handleApiCall } from "../api-error-handler";
import ticketTypesQueryKeys, { ticketingQueryKeys } from "../query-keys/tickets.query-keys";
import { NullablePartial, TicketingType } from "@ac-expo/api-types";
import { TicketingOnlineConfigForm } from "../schemas/tickets/ticketing-online-config.schema";
import { applyTimeToDate, toDatabaseFormat } from "@ac-expo/utils";
import { IssueTicketsWithoutDeliveryModeForm } from "../schemas/tickets/issue-tickets.schema";
import { eventsQueryKeys } from "../query-keys";
import { CreateDiscountCode } from "../schemas";

export const enableTicketing = async (
  eventId: string,
  body?: { bankDetailsId: string; isPaidEntry: boolean },
) => {
  try {
    const { data } = await privateAxios.post<TicketingType>(
      `/events/${eventId}/tickets/enable`,
      body,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useEnableTicketingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      bankDetailsId,
      isPaidEntry,
    }: {
      eventId: string;
      bankDetailsId?: string;
      isPaidEntry?: boolean;
    }) =>
      enableTicketing(
        eventId,
        bankDetailsId ? { bankDetailsId, isPaidEntry: isPaidEntry ?? false } : undefined,
      ),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketingQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

type UpdateTicketingConfigBody = {
  eventId: string;
} & Omit<TicketingOnlineConfigForm, "availabilityStartingDate" | "availabilityEndingDate"> &
  NullablePartial<
    Pick<TicketingOnlineConfigForm, "availabilityStartingDate" | "availabilityEndingDate">
  >;

export const updateTicketingConfig = async ({
  eventId,
  availabilityStartingDate,
  availabilityStartingTime,
  availabilityEndingDate,
  availabilityEndingTime,
  ...body
}: UpdateTicketingConfigBody) => {
  try {
    const { data } = await privateAxios.patch<TicketingType>(
      `/events/${eventId}/tickets/ticketing`,
      {
        ...body,
        availabilityStartingDate:
          availabilityStartingDate && availabilityStartingTime
            ? applyTimeToDate(availabilityStartingDate, availabilityStartingTime)
            : null,
        availabilityEndingDate:
          availabilityEndingDate && availabilityEndingTime
            ? applyTimeToDate(availabilityEndingDate, availabilityEndingTime)
            : null,
      },
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useUpdateTicketingConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTicketingConfig,
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketingQueryKeys.event(eventId),
      });
    },
  });
};

type CreateTicketTypeVariables = CreateTicketTypeForm & { eventId: string };

const getDataFromFormData = ({ saleStartTime, saleEndTime, ...data }: CreateTicketTypeForm) => ({
  ...data,
  validityDates: data.validityDates.map(toDatabaseFormat),
  quantity: data.quantity === "Infini" ? null : data.quantity,
  isQuantityInfinite: data.quantity === "Infini",
  saleStartDate:
    data.saleStartDate && saleStartTime ? applyTimeToDate(data.saleStartDate, saleStartTime) : null,
  saleEndDate:
    data.saleEndDate && saleEndTime ? applyTimeToDate(data.saleEndDate, saleEndTime) : null,
  discountCodes: data.discountCodes.map((code) => ({
    ...code,
    validityDates: code.validityDates.map(toDatabaseFormat),
    quantity: code.quantity === "Infini" ? null : code.quantity,
  })),
});

export const createTicketType = ({ eventId, ...body }: CreateTicketTypeVariables) =>
  handleApiCall(() =>
    privateAxios.post<TicketType>(`/events/${eventId}/tickets/types`, getDataFromFormData(body)),
  );

export const updateTicketType = ({ eventId, ...body }: CreateTicketTypeVariables) =>
  handleApiCall(() =>
    privateAxios.patch<TicketType>(
      `/events/${eventId}/tickets/types/${body.id}`,
      getDataFromFormData(body),
    ),
  );

export const useTicketTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateTicketTypeVariables) =>
      values.id ? updateTicketType(values) : createTicketType(values),
    onSuccess: (_: unknown, { id, eventId }: CreateTicketTypeVariables) => {
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.event(eventId),
      });
      if (id) {
        queryClient.invalidateQueries({
          queryKey: ticketTypesQueryKeys.get(eventId, id),
        });
      }
    },
  });
};

export const deleteTicketType = async ({ eventId, id }: { eventId: string; id: string }) => {
  try {
    const { data } = await privateAxios.delete<TicketType>(
      `/events/${eventId}/tickets/types/${id}`,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useDeleteTicketTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTicketType,
    onSuccess: (_, { eventId, id }) => {
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.get(eventId, id),
      });
    },
  });
};

const getDiscountCodeDataFromFormData = (data: CreateDiscountCode) => ({
  ...data,
  validityDates: data.validityDates.map(toDatabaseFormat),
});

export const createDiscountCode = ({
  eventId,
  ...body
}: {
  eventId: string;
} & CreateDiscountCode) =>
  handleApiCall(() =>
    privateAxios.post<DiscountCode>(
      `/events/${eventId}/tickets/discounts`,
      getDiscountCodeDataFromFormData(body),
    ),
  );

export const useCreateDiscountCodeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDiscountCode,
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketingQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

const deleteDiscountCode = async ({ eventId, id }: { eventId: string; id: string }) => {
  try {
    await privateAxios.delete(`/events/${eventId}/tickets/discounts/${id}`);
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useDeleteDiscountCodeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDiscountCode,
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketingQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

export const issueTickets = async ({
  eventId,
  itemId,
  ...body
}: {
  eventId: string;
} & IssueTicketsWithoutDeliveryModeForm) => {
  try {
    await privateAxios.post(`/events/${eventId}/tickets/types/${itemId}/issue`, body.tickets);
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useIssueTicketsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: issueTickets,
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.event(eventId),
      });
    },
  });
};

export const assignTicket = async ({
  eventId,
  structureId,
  isCommercial,
  ...body
}: {
  eventId: string;
  structureId: string;
  isCommercial: boolean;
} & IssueTicketsWithoutDeliveryModeForm) => {
  try {
    const assignTo = isCommercial ? "assign-to-commercial" : "assign-to-visitor";
    await privateAxios.post(
      `/events/${eventId}/tickets/${assignTo}/${body.itemId}/${structureId}`,
      body.tickets,
    );
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const issueActivityTicket = async ({
  eventId,
  structureId,
  isCommercial,
  ...body
}: {
  eventId: string;
  structureId: string;
  isCommercial: boolean;
} & IssueTicketsWithoutDeliveryModeForm) => {
  try {
    const giveTo = isCommercial ? "give-to-commercial" : "give-to-visitor";
    await privateAxios.post(
      `/events/${eventId}/tickets/activity/${giveTo}/${body.itemId}/${structureId}`,
      body.tickets,
    );
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useIssueActivityTicketMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: issueActivityTicket,
    onSuccess: (_, { eventId, structureId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.get(eventId, structureId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.ticket(eventId, structureId),
      });
    },
  });
};

export const useAssignTicketMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignTicket,
    onSuccess: (_, { eventId, structureId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.get(eventId, structureId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.ticket(eventId, structureId),
      });
    },
  });
};

export const unAssignActivityTicket = async ({
  ticketId,
  eventId,
}: {
  ticketId: string;
  eventId: string;
}) => {
  try {
    await privateAxios.get(`/events/${eventId}/tickets/activity/unset-ticket/${ticketId}`);
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useUnAssignActivityTicketMutation = (structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unAssignActivityTicket,
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.get(eventId, structureId || ""),
      });
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.ticket(eventId, structureId || ""),
      });
    },
  });
};

export const unAssignTicket = async ({
  ticketId,
  eventId,
}: {
  ticketId: string;
  eventId: string;
}) => {
  try {
    await privateAxios.get(`/events/${eventId}/tickets/unset-ticket/${ticketId}`);
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useUnAssignTicketMutation = (structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unAssignTicket,
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.get(eventId, structureId || ""),
      });
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.ticket(eventId, structureId || ""),
      });
    },
  });
};
